import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Activity, Newspaper, MapPin, CloudSun } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function GoalieCard({ side, goalie, team }) {
  return (
    <div data-testid={`goalie-${side}`} className="rounded-lg bg-[#151B2B] border border-[#232D42] p-3">
      <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-2">{team} · Starting Goalie</div>
      {goalie?.name ? (
        <div className="flex items-center gap-2">
          {goalie.headshot && <img src={goalie.headshot} alt="" className="h-8 w-8 rounded-full object-cover" />}
          <div>
            <div className="text-sm font-semibold text-slate-100">{goalie.name}</div>
            {goalie.status && <div className="text-[10px] font-mono text-[#00E5A3] uppercase">{goalie.status}</div>}
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-500">Not confirmed yet — usually locks ~5h before puck drop.</div>
      )}
    </div>
  );
}

export default function GameDetail() {
  const { league, eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/games/${league}/${eventId}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [league, eventId]);

  if (loading) return <div className="text-center py-20 text-slate-500 text-sm">Loading matchup…</div>;
  if (!data?.game && !data?.detail) return <div className="text-center py-20 text-slate-500 text-sm">Game not found.</div>;

  const g = data.game || {};
  const d = data.detail || {};
  const injByTeam = (abbr) => (d.injuries || []).filter((i) => i.team === abbr);
  const isNHL = league === "NHL";

  return (
    <div data-testid="game-detail-page" className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
      <Link to="/games" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#00E5A3] mb-4">
        <ArrowLeft size={14} /> All games
      </Link>

      {/* Header */}
      <div className="rounded-xl bg-[#0F1420] border border-[#232D42] p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-[#151B2B] text-slate-300 border border-[#232D42] text-[10px] font-mono">{g.league || league}</Badge>
          <span className="text-[11px] font-mono text-slate-500">{fmtDate(g.date)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          {[g.away, g.home].map((t, i) => (
            <div key={i} className={`flex-1 flex flex-col items-center text-center ${i === 0 ? "" : ""}`}>
              {t?.logo && <img src={t.logo} alt="" className="h-14 w-14 object-contain mb-2" />}
              <div className="font-heading font-bold text-slate-100">{t?.name || t?.abbr}</div>
              {t?.record && <div className="text-[10px] font-mono text-slate-500">{t.record}</div>}
              {t?.score != null && t?.score !== "" && <div className="font-mono text-2xl text-slate-200 mt-1">{t.score}</div>}
              {i === 0 && <div className="text-slate-600 font-mono text-xs mt-2">@</div>}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t border-[#232D42] text-[11px] font-mono text-slate-500">
          {g.venue?.name && <span className="flex items-center gap-1"><MapPin size={12} /> {g.venue.name}</span>}
          {g.weather?.temperature != null && g.venue?.indoor === false && (
            <span className="flex items-center gap-1 text-[#38BDF8]"><CloudSun size={12} /> {g.weather.temperature}° · {g.weather.conditionId}</span>
          )}
          {(d.odds?.details || g.odds?.details) && (
            <span className="text-slate-400">Line: {d.odds?.details || g.odds?.details}{(d.odds?.over_under || g.odds?.over_under) ? ` · O/U ${d.odds?.over_under || g.odds?.over_under}` : ""}</span>
          )}
        </div>
      </div>

      {/* Probable starters — NHL goalies / MLB pitchers */}
      {(g.away?.starter || g.home?.starter) && (
        <div className="mb-5">
          <h2 className="font-heading font-bold text-lg text-slate-100 mb-2 flex items-center gap-2">
            <ShieldAlert size={18} className="text-[#00E5A3]" /> {league === "MLB" ? "Probable Pitchers" : "Starting Goalies"}
            <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">HIGH IMPACT</Badge>
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <GoalieCard side="away" team={g.away?.abbr} goalie={g.away?.starter} />
            <GoalieCard side="home" team={g.home?.abbr} goalie={g.home?.starter} />
          </div>
        </div>
      )}

      {/* NBA starting lineup + late scratches */}
      {league === "NBA" && (
        <div className="mb-5">
          <h2 className="font-heading font-bold text-lg text-slate-100 mb-2 flex items-center gap-2">
            <ShieldAlert size={18} className="text-[#00E5A3]" /> Starting Lineups
            <Badge className="bg-[#00E5A3]/15 text-[#00E5A3] border-0 text-[10px] font-mono">~30 MIN PRE-TIP</Badge>
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["away", g.away], ["home", g.home]].map(([sd, t]) => {
              const starters = (d.lineups && d.lineups[sd]) || [];
              return (
                <div key={sd} data-testid={`lineup-${t?.abbr}`} className="rounded-lg bg-[#151B2B] border border-[#232D42] p-3">
                  <div className="text-[11px] uppercase tracking-wider font-mono text-slate-500 mb-2">{t?.name || t?.abbr}</div>
                  {starters.length ? starters.map((p, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-[#232D42]/40 last:border-0">
                      <span className="text-slate-200">{p.name}</span>
                      <span className="text-slate-500 font-mono">{p.pos}</span>
                    </div>
                  )) : <div className="text-xs text-slate-500">Not posted yet — confirmed lineups drop ~30 min before tip-off.</div>}
                </div>
              );
            })}
          </div>
          {d.inactives && d.inactives.length > 0 && (
            <div className="mt-3 rounded-lg border border-[#FF6B6B]/30 bg-[#FF6B6B]/5 p-3">
              <div className="text-[11px] uppercase tracking-wider font-mono text-[#FF6B6B] mb-1">Late scratches / Out</div>
              <div className="flex flex-wrap gap-2">
                {d.inactives.map((x, i) => (
                  <span key={i} data-testid={`scratch-${i}`} className="text-xs text-slate-200 bg-[#151B2B] border border-[#232D42] rounded px-2 py-1">
                    {x.name} <span className="text-slate-500 font-mono">{x.team} · {x.pos}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Injuries both sides */}
      <div className="mb-5">
        <h2 className="font-heading font-bold text-lg text-slate-100 mb-2 flex items-center gap-2">
          <Activity size={18} className="text-[#FF6B6B]" /> Injuries & Status
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {[g.away, g.home].map((t, i) => {
            const inj = injByTeam(t?.abbr);
            return (
              <div key={i} data-testid={`injuries-${t?.abbr}`} className="rounded-lg bg-[#0F1420] border border-[#232D42] p-3">
                <div className="text-[11px] uppercase tracking-wider font-mono text-slate-500 mb-2">{t?.name || t?.abbr}</div>
                {inj.length ? inj.slice(0, 8).map((x, j) => (
                  <div key={j} className="flex justify-between text-xs py-1 border-b border-[#232D42]/50 last:border-0">
                    <span className="text-slate-200">{x.name} <span className="text-slate-500 font-mono">{x.pos}</span></span>
                    <span className="text-[#FF6B6B] font-mono uppercase text-[10px]">{x.status}</span>
                  </div>
                )) : <div className="text-xs text-slate-500">No reported injuries.</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Matchup news */}
      {d.news && d.news.length > 0 && (
        <div className="mb-5">
          <h2 className="font-heading font-bold text-lg text-slate-100 mb-2 flex items-center gap-2">
            <Newspaper size={18} className="text-[#38BDF8]" /> Matchup News
          </h2>
          <div className="space-y-2">
            {d.news.map((n, i) => (
              <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                data-testid={`game-news-${i}`}
                className="block text-sm text-slate-300 hover:text-[#00E5A3] rounded-lg bg-[#0F1420] border border-[#232D42] px-3 py-2">
                {n.headline}
              </a>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-600 mt-6">
        Information sourced from ESPN. For research only — not betting advice.
      </p>
    </div>
  );
}
